import { FC, useMemo } from "react";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import { OrderOptimizerCargoListingItem } from "../common/types/order-optimizer-cargo-listing-item";
import OrderOptimizerOptimizationStatus from "../../../../../common/services/order-optimizer/optimization-status/order-optimizer-optimization-status";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerCargoListingTableColumn from "../common/types/order-optimizer-cargo-listing-table-column";
import orderOptimizerCargoListingTableHelper from "./order-optimizer-cargo-listing-table.helpers";
import useAbort from "../../../../../common/hooks/use-abort";
import OrderOptimizerOptimizationStatusParams from "../../../../../common/services/order-optimizer/optimization-status/order-optimizer-optimization-status-params";
import orderOptimizerService from "../../../../../common/services/order-optimizer/order-optimizer.service";
import orderOptimizerRoutesHelper from "../../../common/routes/order-optimizer-routes.helper";
import notificationService from "../../../../../common/utils/notification/notification.service";
import OrderOptimizerCargoListingTableRow from "../common/types/order-optimizer-cargo-listing-table-row";
import OrderOptimizerCargoListingTableRowComponent from "./row/order-optimizer-cargo-listing-table-row.component";

type OrderOptimizerCargoListingTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  listingItems: OrderOptimizerCargoListingItem[];
  selectedHumanId: number | undefined;
  applyOptimizationStatus: (
    optimizationStatus: OrderOptimizerOptimizationStatus,
    optimizationId: string,
  ) => void;
};

const OrderOptimizerCargoListingTableComponent: FC<
  OrderOptimizerCargoListingTableProps
> = (props) => {
  const { selectedAppLanguage, user } = useAppContext();
  const translation =
    orderOptimizerTranslationsHelper.getCargoListingTranslations().table;

  const columns = useMemo(
    (): OrderOptimizerCargoListingTableColumn[] =>
      orderOptimizerCargoListingTableHelper.getColumns(),
    [],
  );

  const orderOptimizerOptimizationStatusAbort = useAbort();

  const onOptimizationClick = async (listingItemUuid: string) => {
    const optimizationStatusLoadParams: OrderOptimizerOptimizationStatusParams =
      {
        optimizationId: listingItemUuid,
      };

    await orderOptimizerService
      .getOptimizationStatus(
        optimizationStatusLoadParams,
        orderOptimizerOptimizationStatusAbort.signal,
      )
      .then((optimizationStatus) => {
        if (
          !!optimizationStatus.reservedAt &&
          !!optimizationStatus.reservedBy
        ) {
          props.applyOptimizationStatus(optimizationStatus, listingItemUuid);
        } else {
          const currentUserStatus: OrderOptimizerOptimizationStatus = {
            reservedAt: new Date(),
            reservedBy: `${user?.firstName} ${user?.lastName}`,
          };

          props.applyOptimizationStatus(currentUserStatus, listingItemUuid);
        }
      })
      .then(() => {
        window.open(
          orderOptimizerRoutesHelper.getDetailsRoute({
            id: listingItemUuid,
          }),
          "_blank",
        );
      })
      .catch(() => {
        notificationService.error(
          translation.optimizationReservationErrorNotification,
        );
      });
  };

  const createTableRow = (
    listingItem: OrderOptimizerCargoListingItem,
  ): OrderOptimizerCargoListingTableRow => {
    return {
      id: listingItem.id,
      value: {
        row: <></>,
      },
      hiddenComponent: (
        <OrderOptimizerCargoListingTableRowComponent
          listingItem={listingItem}
          selectedHumanId={props.selectedHumanId}
          onOptimizationRequestClick={() => onOptimizationClick(listingItem.id)}
        />
      ),
      isHiddenComponentVisible: true,
    };
  };

  const rows: OrderOptimizerCargoListingTableRow[] = useMemo(() => {
    return props.listingItems.map((list) => createTableRow(list));
  }, [props.listingItems, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      noHeader
    />
  );
};

export default OrderOptimizerCargoListingTableComponent;
